import React from 'react';
import { Col, Row, Tag, Typography } from 'antd';

const { Text } = Typography;

import BuildingContainer from '@totem/components/portfolio/buildings/details/container/BuildingContainer';
import {
  calculateAnnualSavings,
  calculateOptimizeROIPercent,
  calculateOptimizeROIValue,
  calculatePaybackMonths,
  formatPaybackPeriod,
  formatSavingsTimeframe,
  getImplementedAt,
} from '@totem/components/ticketing/ticket/specialCases/utilities';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import Currency from '@totem/components/ui_components/widgets/currency/Currency';
import { formatDate, isNotNull, isNull } from '@totem/utilities/common';

const EFFORT_COLORS: Record<string, string> = {
  Low: 'green',
  Medium: 'orange',
  High: 'red',
};

const RETURN_COLORS: Record<string, string> = {
  Low: 'default',
  Medium: 'blue',
  High: 'purple',
};

const pillStyle: React.CSSProperties = {
  borderRadius: 6,
  padding: '4px 12px',
  fontWeight: 600,
  fontSize: 12,
};

const getApprovalLabel = (rec: TicketQueryResult): string | null => {
  const reqs = rec.ticket.approvalRequests;

  if (isNull(reqs) || reqs.length === 0) {
    return null;
  }

  if (reqs.findIndex((chk) => chk.status === 'APPROVED') >= 0) {
    return 'Approved';
  }

  if (reqs.findIndex((chk) => chk.status === 'REJECTED') >= 0) {
    return 'Rejected';
  }

  if (reqs.findIndex((chk) => chk.status === 'SENT') >= 0) {
    return 'Pending Approval';
  }

  return null;
};

const ExpandedRow = ({ record }: { record: TicketQueryResult }) => {
  const opt = record.ticket.extras?.optimize ?? null;
  const approvalLabel = getApprovalLabel(record);
  const annualSavings = isNotNull(opt) ? calculateAnnualSavings(opt) : null;
  const paybackMonths = isNotNull(opt) ? calculatePaybackMonths(opt) : null;
  const roiValue = isNotNull(opt) ? calculateOptimizeROIValue(opt) : null;
  const roiPercent = isNotNull(opt) ? calculateOptimizeROIPercent(opt) : null;
  const timeframe = isNotNull(opt) ? formatSavingsTimeframe(opt) : null;
  const implementedAt = isNotNull(opt)
    ? getImplementedAt(
        record.ticket.status,
        record.ticket.subStatus,
        opt.implementationDate,
        record.ticket.closedAt,
        record.ticket.resolvedAt,
      )
    : null;

  const panelStyle: React.CSSProperties = {
    border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: 8,
    padding: '16px 20px',
    height: '100%',
  };

  return (
    <div style={{ padding: '12px 8px 16px' }}>
      <Row gutter={[16, 16]}>
        {/* Left panel — header + description */}
        <Col xs={24} lg={14}>
          <div style={panelStyle}>
            <Row justify="space-between" style={{ marginBottom: 12 }}>
              <Col>
                <Text>
                  <strong>Value Creation Opportunity: </strong>
                  <Text style={{ color: '#0ea5e9' }}>
                    {opt?.category ?? record.ticket.category}
                  </Text>
                </Text>
              </Col>
              {record.building?.name && (
                <Col>
                  <Text>
                    <strong>Property: </strong>
                    {record.building.name}
                  </Text>
                </Col>
              )}
            </Row>
            {record.ticket.description ? (
              <div style={{ whiteSpace: 'pre-wrap' }}>
                <Text>{record.ticket.description}</Text>
              </div>
            ) : (
              <Text type="secondary" style={{ fontStyle: 'italic' }}>
                No description provided.
              </Text>
            )}
          </div>
        </Col>

        {/* Right panel — tags + steps required */}
        <Col xs={24} lg={10}>
          <div style={panelStyle}>
            <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
              {isNotNull(opt) && opt.levelOfEffort && (
                <Col>
                  <Tag
                    color={EFFORT_COLORS[opt.levelOfEffort] ?? 'default'}
                    style={pillStyle}
                  >
                    Effort: {opt.levelOfEffort}
                  </Tag>
                </Col>
              )}
              {isNotNull(opt) && opt.scopeOfReturn && (
                <Col>
                  <Tag
                    color={RETURN_COLORS[opt.scopeOfReturn] ?? 'default'}
                    style={pillStyle}
                  >
                    Return: {opt.scopeOfReturn}
                  </Tag>
                </Col>
              )}
              {approvalLabel && (
                <Col>
                  <Tag color="blue" style={pillStyle}>
                    {approvalLabel}
                  </Tag>
                </Col>
              )}
            </Row>
            {isNotNull(opt) && opt.stepsRequired ? (
              <>
                <Text strong>Steps Required:</Text>
                <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                  <Text>{opt.stepsRequired}</Text>
                </div>
              </>
            ) : (
              <Text type="secondary" style={{ fontStyle: 'italic' }}>
                No steps recorded.
              </Text>
            )}
          </div>
        </Col>
      </Row>

      {/* Bottom metrics strip */}
      {isNotNull(opt) && (
        <Row
          gutter={[16, 0]}
          style={{
            marginTop: 12,
            borderRadius: 8,
            padding: '10px 20px',
            background: 'rgba(0,0,0,0.04)',
          }}
          align="middle"
        >
          <Col>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Savings
            </Text>
            <br />
            <Text strong style={{ fontSize: 13 }}>
              <Currency amount={opt.savingsPotential} />/
              {opt.savingsTermPeriod
                .replace('Months', 'mo')
                .replace('Years', 'yr')}{' '}
              <Text type="secondary" style={{ fontSize: 11 }}>
                for {timeframe}
              </Text>
            </Text>
          </Col>
          {annualSavings !== null &&
            (opt.savingsTermPeriod === 'Month' ||
              opt.savingsTermPeriod === 'Months') && (
              <Col>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Annual
                </Text>
                <br />
                <Text strong style={{ fontSize: 13 }}>
                  <Currency amount={annualSavings} />
                  /yr
                </Text>
              </Col>
            )}
          {opt.requiredInvestment > 0 && (
            <Col>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Investment
              </Text>
              <br />
              <Text strong style={{ fontSize: 13 }}>
                <Currency amount={opt.requiredInvestment} />
              </Text>
            </Col>
          )}
          {paybackMonths !== null && (
            <Col>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Payback
              </Text>
              <br />
              <Text strong style={{ fontSize: 13 }}>
                {formatPaybackPeriod(paybackMonths)}
              </Text>
            </Col>
          )}
          {isNotNull(roiPercent) && !Number.isNaN(roiPercent) && (
            <Col>
              <Text type="secondary" style={{ fontSize: 11 }}>
                ROI
              </Text>
              <br />
              <Text strong style={{ fontSize: 13 }}>
                <Currency amount={roiValue} /> (
                {roiPercent.toLocaleString('en-US', {
                  maximumFractionDigits: 0,
                })}
                %)
              </Text>
            </Col>
          )}
          {implementedAt && (
            <Col>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Implemented
              </Text>
              <br />
              <Text strong style={{ fontSize: 13 }}>
                {formatDate(implementedAt)}
              </Text>
            </Col>
          )}
        </Row>
      )}
      <BuildingContainer id={record.building.id}>
        <div>Add Building Details Here</div>
      </BuildingContainer>
    </div>
  );
};

export default ExpandedRow;
